import { select, put } from "redux-saga/effects";
import { ProgramAvailablity, ProgramStatus } from "src/enums";
import { ProgramMessage } from "src/models";
import { ProgramDetail, UserSession, ProgramViewAction, TvAction } from "src/store";
import { _ } from "..";

export namespace PlayerMessageService {
    const getProgramMessage = (availability?: ProgramAvailablity, status?: number, data?: any, customMessage?: string): ProgramMessage => {

        data = data || {};

        const message: ProgramMessage = {
            hasMessage: availability !== undefined && availability !== ProgramAvailablity.Available,
            hideTitle: data.hideTitle,
            message: customMessage || data.message,
            title: data.title,
            status,
            links: data._links,
            submit: data.submit,
            link: data.link
        };

        if (availability === undefined) return message;
        if (availability === ProgramAvailablity.Available) return message;

        switch (availability) {
            case ProgramAvailablity.Simultaneous:
                message.message = message.message || _("simultaneousPlayMessage");
                message.title = message.title || _("simultaneousPlayTitle");
                message.logo = "artwork-simultaneous.svg";
                break;
            case ProgramAvailablity.Locked:
                message.message = message.message || _("videoLockedMessage");
                message.title = message.title || _("videoLockedTitle");
                message.logo = "artwork-lock.svg";
                break;
            case ProgramAvailablity.Forbiden:
                message.message = message.message || _("forbidenPlayMessage");
                message.title = message.title || _("forbidenPlayTitle");
                message.logo = "artwork-vpn.svg";
                break;
            case ProgramAvailablity.Finished:
                message.message = message.message || _("videoEndedMessage");
                message.title = message.title || _("videoEndedTitle");
                message.logo = "artwork-end-program.svg";
                break;
            case ProgramAvailablity.NeedPackage:
                message.message = message.message || _("needPackageMessage");
                message.title = message.title || _("needPackageTitle");
                message.logo = "big-lock.svg";
                break;
            case ProgramAvailablity.QualityNeedPackageCountdown:
            case ProgramAvailablity.NeedPackageCountDown:
                message.title = message.title || _("untillStart");
                message.message = message.message || _("needPackageMessage");
                message.logo = "big-lock.svg";
                break;
            case ProgramAvailablity.CountDown:
                message.title = message.title || _("untillStart");
                break;
            case ProgramAvailablity.Unauthorized:
                message.message = message.message || _("unauthorizedPlayMessage");
                message.title = message.title || _("unauthorizedPlayTitle");
                message.logo = "artwork-login.svg";
                break;
            case ProgramAvailablity.CominSoon:
                message.message = message.message || _("comingSoonMessage");
                message.title = message.title || _("comingSoonTitle");
                message.logo = "artwork-lock.svg";
                break;
            case ProgramAvailablity.CapacityExceed:
                message.message = message.message || _("capacityExceedMessage");
                message.title = message.title || _("capacityExceedTitle");
                message.logo = "artwork-vpn.svg";
                break;
            case ProgramAvailablity.DeviceAccessTerminated:
                message.message = message.message || _("deviceTerminatedMessage");
                message.title = message.title || _("deviceTerminatedTitle");
                message.logo = "artwork-simultaneous.svg";
                break;
            case ProgramAvailablity.MustRedirect:
                message.logo = "external-link.svg";
                break;
            case ProgramAvailablity.Missing:
            case ProgramAvailablity.NotFound:
            default:
                message.message = message.message || _("videoMissingMessage");
                message.title = message.title || _("videoMissingTitle");
                message.logo = "artwork-no-video.svg";
        }

        return message;
    };

    export const setProgramAvailabilityByInfo = function* () {
        const programInfo: ProgramDetail = yield select((state: any) => state.ProgramViewState);
        const user: UserSession = yield select((state: any) => state.UserState);

        const info = programInfo.info;

        if (!info) return false;

        const started = info.isLive || info.status === ProgramStatus.Started || info.status === ProgramStatus.Archived || !info.remainingToStart;

        let availability = ProgramAvailablity.Available;
        let customMessage: string | undefined;
        let data: any;

        if (info.originConfig && info.originConfig.enabled) {
            availability = ProgramAvailablity.MustRedirect;
            data = {
                message: info.originConfig.message,
                title: info.originConfig.title,
                submit: info.originConfig.submit,
                link: info.originConfig.link
            };
        }
        else if (!programInfo.currentStream) {
            availability = info.streams && info.streams.length > 0 ?
                !user.loggedIn ? ProgramAvailablity.Unauthorized :
                    started ? ProgramAvailablity.NeedPackage : ProgramAvailablity.NeedPackageCountDown :
                ProgramAvailablity.NotFound;
        }
        else if (!user.loggedIn) {
            availability = ProgramAvailablity.Unauthorized;
        }
        else if (info.lock) {
            availability = started ? ProgramAvailablity.NeedPackage : ProgramAvailablity.NeedPackageCountDown;
        }
        else if (!started) {
            if (info.streams?.some((x) => x.subs?.some((y) => y.lock))) {
                availability = ProgramAvailablity.QualityNeedPackageCountdown;
            }
            else {
                availability = ProgramAvailablity.CountDown;
            }
        }

        const needPackage = [ProgramAvailablity.NeedPackage, ProgramAvailablity.NeedPackageCountDown, ProgramAvailablity.QualityNeedPackageCountdown].includes(availability);
        if (needPackage) customMessage = info.promotionalText || customMessage;

        yield put(ProgramViewAction.setProgramMessage(getProgramMessage(availability, undefined, data, customMessage)));
        yield put(ProgramViewAction.setProgramStatus(availability));
        return availability === ProgramAvailablity.Available;

    };

    export const setProgramLocked = function* () {
        const programInfo: ProgramDetail = yield select((state: any) => state.ProgramViewState);

        const info = programInfo.info;

        if (!info) return;

        let availability = ProgramAvailablity.NeedPackage;
        let customMessage: string | undefined = info.promotionalText;
        yield put(ProgramViewAction.setProgramMessage(getProgramMessage(availability, undefined, undefined, customMessage)));
        yield put(ProgramViewAction.setProgramStatus(availability));
    };

    export const setProgramAvailabilityByStatus = function* (data: any, status?: number) {

        let availability = ProgramAvailablity.Missing;

        switch (status) {
            case 401:
                availability = ProgramAvailablity.Simultaneous;
                if (data && data.sessions) {
                    yield put(ProgramViewAction.setOtherSessions((data.sessions || []).map((x: any) => x.id)));
                }
                break;
            case 402:
                availability = ProgramAvailablity.Locked;
                break;
            case 403:
                availability = ProgramAvailablity.Forbiden;
                break;
            case 406:
                availability = ProgramAvailablity.CapacityExceed;
                break;
            case 423:
                availability = data.status === 4 ? ProgramAvailablity.Finished :
                    data.status === 1 ? ProgramAvailablity.CominSoon : ProgramAvailablity.Locked;
                break;
            case 422:
                availability = ProgramAvailablity.DeviceAccessTerminated;
                break;
            case 308:
                availability = ProgramAvailablity.MustRedirect;
                break;
            case 404:
            default:
                availability = ProgramAvailablity.Missing;
        }

        yield put(ProgramViewAction.setProgramMessage(getProgramMessage(availability, status, data)));
        yield put(ProgramViewAction.setProgramStatus(availability));

    };


    export const setTvAvailabilityByStatus = function* (data: any, status?: number) {


        let availability = ProgramAvailablity.Missing;

        switch (status) {
            case 401:
                availability = ProgramAvailablity.Simultaneous;
                if (data && data.sessions) {
                    yield put(TvAction.setOtherSessions((data.sessions || []).map((x: any) => x.id)));
                }
                break;
            case 403:
                availability = ProgramAvailablity.Forbiden;
                break;
            case 404:
            default:
                availability = ProgramAvailablity.Missing;
        }

        yield put(TvAction.setTvMessage(getProgramMessage(availability, status, data)));
        yield put(TvAction.setTvStatus(availability));

    };

}